// Shared API base + call helpers for ParkConnect (/parkconnect and
// /parkconnectadmin).
//
// In dev the Express API runs on :3001; in prod it's same-origin (Vercel).
// Reads of the listing board are public; the admin page logs in with the shared
// APP_PASSWORD via /api/auth and sends the returned token as a Bearer header on
// every moderation call.

export const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
const ENDPOINT = `${API_BASE}/api/parkconnect`;

/** Throws with the server's own message, which is written to be shown as-is. */
async function unwrap(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.ok === false) {
    const err = new Error(body.error || 'That did not go through. Try again in a moment.');
    err.status = res.status;
    throw err;
  }
  return body;
}

/** The public board: approved listings with at least one day still ahead. */
export async function fetchListings() {
  const res = await fetch(ENDPOINT, { headers: { Accept: 'application/json' } });
  const body = await unwrap(res);
  return Array.isArray(body.listings) ? body.listings : [];
}

/** Both moderation queues in full. `token` comes from /api/auth. */
export async function fetchAdminData(token, limit = 300) {
  const res = await fetch(`${ENDPOINT}?part=admin&limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return unwrap(res);
}

export function post(op, body, token) {
  return fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ op, ...body }),
  }).then(unwrap);
}

export const submitListing = (listing) => post('submit-listing', listing);
export const submitRequest = (request) => post('submit-request', request);

/**
 * Send one document up before the form itself, so a submission only ever
 * carries the stored reference. Returns { url, name } to put on the payload.
 */
export function uploadDoc(file, kind) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.onload = () => {
      post('upload', { kind, filename: file.name, dataUrl: reader.result }).then(resolve, reject);
    };
    reader.readAsDataURL(file);
  });
}

export async function login(password) {
  const res = await fetch(`${API_BASE}/api/auth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.token) throw new Error(body.error || 'Login failed.');
  return body.token;
}
